import React from 'react';

/**
 * Setup Components Library
 * Generic React components for visualizing board game setups
 * AI generates JSON that maps to these components
 */

// ============================================
// SETUP TABLE - The game table surface
// ============================================
export const SetupTable = ({ 
    shape = 'rectangle', 
    children, 
    style = {},
    theme = {}
}) => {
    const baseStyle = {
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '500px',
        background: theme.tableColor || 'linear-gradient(135deg, #2d4a3e 0%, #1a2f27 100%)',
        borderRadius: shape === 'round' ? '50%' : '24px',
        border: `4px solid ${theme.borderColor || '#4a3728'}`,
        boxShadow: 'inset 0 4px 30px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.5)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
    };

    return (
        <div className="setup-table" style={baseStyle}>
            {children}
        </div>
    );
};

// ============================================
// SETUP BOARD - Game board (overlay, track, path, area)
// ============================================
export const SetupBoard = ({ 
    variant = 'overlay', // overlay, track, path, area, grid
    rows = 4,
    cols = 4,
    label = '',
    position = { x: 50, y: 50 },
    size = { w: 40, h: 30 },
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {},
    children
}) => {
    const baseStyle = {
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        width: `${size.w}%`,
        height: `${size.h}%`,
        borderRadius: '6px',
        boxShadow: isActive 
            ? `0 0 0 3px ${theme.highlightColor || '#ffd700'}, 0 4px 12px rgba(255,215,0,0.3)`
            : '0 2px 8px rgba(0,0,0,0.3)',
        cursor: isHotspot ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        overflow: 'hidden'
    };

    // Subtle overlay - doesn't obstruct the game image behind
    const renderOverlay = () => (
        <div style={{
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.2)',
            border: `2px dashed ${theme.borderColor || 'rgba(255,255,255,0.5)'}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {label && (
                <span style={{ 
                    color: '#fff', 
                    fontSize: '0.7rem', 
                    fontWeight: 'bold',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                    padding: '4px 8px',
                    background: 'rgba(0,0,0,0.4)',
                    borderRadius: '4px'
                }}>
                    {label}
                </span>
            )}
        </div>
    );

    // Small grid overlay (not chess-like)
    const renderGrid = () => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            width: '100%',
            height: '100%',
            gap: '1px',
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '6px',
            overflow: 'hidden'
        }}>
            {Array.from({ length: rows * cols }).map((_, i) => (
                <div key={i} style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.1)'
                }} />
            ))}
        </div>
    );

    const renderTrack = () => (
        <div style={{
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderRadius: '6px'
        }}>
            <svg width="100%" height="100%" viewBox="0 0 100 60" preserveAspectRatio="none">
                <ellipse 
                    cx="50" cy="30" rx="42" ry="22" 
                    fill="none" 
                    stroke={theme.trackColor || 'rgba(255,255,255,0.6)'} 
                    strokeWidth="6"
                    strokeDasharray="8,4"
                />
                <circle cx="92" cy="30" r="4" fill="#00ff00" />
                <text x="50" y="34" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
                    🏁
                </text>
            </svg>
        </div>
    );

    const renderPath = () => (
        <div style={{
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '6px'
        }}>
            <svg width="100%" height="100%" viewBox="0 0 100 60" preserveAspectRatio="none">
                <path
                    d="M 5,30 C 20,10 35,50 50,30 S 80,10 95,30"
                    stroke={theme.pathColor || 'rgba(139,69,19,0.8)'}
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                />
                <circle cx="5" cy="30" r="3" fill="#00ff00" />
                <circle cx="95" cy="30" r="3" fill="#ff4444" />
            </svg>
        </div>
    );

    // General area marker
    const renderArea = () => (
        <div style={{
            width: '100%',
            height: '100%',
            background: `linear-gradient(135deg, ${theme.areaColor || 'rgba(59,130,246,0.3)'} 0%, ${theme.areaColor || 'rgba(59,130,246,0.1)'} 100%)`,
            border: `2px solid ${theme.areaBorder || 'rgba(59,130,246,0.6)'}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {label && (
                <span style={{ 
                    color: '#fff', 
                    fontSize: '0.65rem', 
                    fontWeight: 'bold',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}>
                    {label}
                </span>
            )}
        </div>
    );

    return (
        <div 
            className={`setup-board ${variant} ${isActive ? 'active' : ''}`}
            style={baseStyle}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label}
        >
            {variant === 'overlay' && renderOverlay()}
            {variant === 'grid' && renderGrid()}
            {variant === 'track' && renderTrack()}
            {variant === 'path' && renderPath()}
            {variant === 'area' && renderArea()}
            {children}
        </div>
    );
};

// ============================================
// SETUP CARDS - Card decks and hands
// ============================================
export const SetupCards = ({ 
    variant = 'deck', // deck, spread, hand, discard, faceup
    count = 1,
    label = '',
    position = { x: 50, y: 50 },
    color = '#1e3a5f',
    faceColor = '#f5f5f0', // Card face color for faceup cards
    pattern = 'diamond', // Card back pattern: diamond, circle, lines, solid
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const cardWidth = 36;
    const cardHeight = 50;
    
    const containerStyle = {
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        cursor: isHotspot ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
    };

    // Card back pattern SVG
    const getPatternStyle = () => {
        const patternColor = adjustColor(color, 20);
        switch (pattern) {
            case 'diamond':
                return {
                    backgroundImage: `
                        linear-gradient(45deg, ${patternColor} 25%, transparent 25%),
                        linear-gradient(-45deg, ${patternColor} 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, ${patternColor} 75%),
                        linear-gradient(-45deg, transparent 75%, ${patternColor} 75%)
                    `,
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0'
                };
            case 'circle':
                return {
                    backgroundImage: `radial-gradient(circle, ${patternColor} 2px, transparent 2px)`,
                    backgroundSize: '6px 6px'
                };
            case 'lines':
                return {
                    backgroundImage: `repeating-linear-gradient(45deg, ${patternColor}, ${patternColor} 1px, transparent 1px, transparent 4px)`,
                };
            default:
                return {};
        }
    };

    const renderCardBack = (index, offsetX = 0, offsetY = 0, rotation = 0) => (
        <div 
            key={index}
            style={{
                position: 'absolute',
                bottom: offsetY,
                left: offsetX,
                width: cardWidth,
                height: cardHeight,
                background: `linear-gradient(145deg, ${color} 0%, ${adjustColor(color, -25)} 100%)`,
                borderRadius: '4px',
                border: '1.5px solid rgba(255,255,255,0.4)',
                boxShadow: `
                    0 ${2 + index}px ${4 + index * 2}px rgba(0,0,0,0.3),
                    inset 0 0 0 2px ${adjustColor(color, -10)},
                    inset 0 1px 0 rgba(255,255,255,0.2)
                `,
                transform: rotation ? `rotate(${rotation}deg)` : 'none',
                overflow: 'hidden'
            }}
        >
            {/* Pattern overlay */}
            <div style={{
                position: 'absolute',
                inset: 4,
                borderRadius: '2px',
                border: '1px solid rgba(255,255,255,0.15)',
                ...getPatternStyle(),
                opacity: 0.3
            }} />
            {/* Center emblem */}
            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    width: cardWidth * 0.4,
                    height: cardWidth * 0.4,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${adjustColor(color, 30)} 0%, ${color} 100%)`,
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
                }} />
            </div>
        </div>
    );

    const renderDeck = () => {
        const stackCount = Math.min(count > 20 ? 5 : count > 10 ? 4 : count > 5 ? 3 : count, 5);
        return (
            <div style={{ 
                position: 'relative', 
                width: cardWidth + stackCount, 
                height: cardHeight + stackCount * 2 
            }}>
                {Array.from({ length: stackCount }).map((_, i) => 
                    renderCardBack(i, i * 0.8, i * 1.5)
                )}
            </div>
        );
    };

    const renderSpread = () => (
        <div style={{ 
            display: 'flex', 
            position: 'relative',
            width: cardWidth + (Math.min(count, 5) - 1) * 12,
            height: cardHeight + 10
        }}>
            {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
                <div 
                    key={i}
                    style={{
                        position: 'absolute',
                        left: i * 12,
                        bottom: Math.abs(i - 2) * 2,
                        width: cardWidth,
                        height: cardHeight,
                        background: `linear-gradient(145deg, ${color} 0%, ${adjustColor(color, -25)} 100%)`,
                        borderRadius: '4px',
                        border: '1.5px solid rgba(255,255,255,0.3)',
                        transform: `rotate(${(i - 2) * 8}deg)`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        zIndex: i
                    }}
                />
            ))}
        </div>
    );

    const renderFaceup = () => (
        <div style={{
            width: cardWidth,
            height: cardHeight,
            background: `linear-gradient(145deg, ${faceColor} 0%, ${adjustColor(faceColor, -10)} 100%)`,
            borderRadius: '4px',
            border: '1px solid rgba(0,0,0,0.2)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.6rem',
            color: '#333',
            fontWeight: 'bold'
        }}>
            🃏
        </div>
    );

    return (
        <div 
            className={`setup-cards ${variant} ${isActive ? 'active' : ''}`}
            style={{
                ...containerStyle,
                background: isActive ? 'rgba(255,215,0,0.15)' : 'transparent',
                padding: '6px',
                borderRadius: '8px',
                boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none'
            }}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label || 'Korttipakka'}
        >
            {variant === 'deck' && renderDeck()}
            {variant === 'spread' && renderSpread()}
            {variant === 'hand' && renderSpread()}
            {variant === 'discard' && renderDeck()}
            {variant === 'faceup' && renderFaceup()}
            {label && (
                <div style={{
                    fontSize: '0.6rem',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                    marginTop: '2px'
                }}>
                    {label} {count > 1 && `(${count})`}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP TOKENS - Game pieces and resources (Carcassonne-style meeples)
// ============================================
export const SetupTokens = ({ 
    variant = 'circle', // circle, cube, meeple, resource, disc, worker
    count = 1,
    color = '#e74c3c',
    colors = null, // Array of colors for multi-colored token groups
    label = '',
    position = { x: 50, y: 50 },
    size = 18,
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const containerStyle = {
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        maxWidth: '100px',
        justifyContent: 'center',
        cursor: isHotspot ? 'pointer' : 'default',
        padding: '5px',
        borderRadius: '6px',
        background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.25)',
        boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none',
        transition: 'all 0.3s ease'
    };

    // Get color for specific token index (supports multi-color arrays)
    const getTokenColor = (index) => {
        if (colors && Array.isArray(colors) && colors.length > 0) {
            return colors[index % colors.length];
        }
        return color;
    };

    // Carcassonne-style meeple SVG path
    const renderMeeple = (index, tokenColor) => {
        const meepleSize = size * 1.2;
        const rotation = (index * 15) - 15;
        return (
            <svg 
                key={index}
                width={meepleSize} 
                height={meepleSize} 
                viewBox="0 0 100 100"
                style={{
                    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))',
                    transform: `rotate(${rotation}deg)`
                }}
            >
                <defs>
                    <linearGradient id={`meepleGrad-${index}-${tokenColor.replace('#', '')}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={adjustColor(tokenColor, 30)} />
                        <stop offset="50%" stopColor={tokenColor} />
                        <stop offset="100%" stopColor={adjustColor(tokenColor, -30)} />
                    </linearGradient>
                </defs>
                {/* Classic Carcassonne meeple shape */}
                <path 
                    d="M50 8 
                       C58 8 64 14 64 22 C64 28 60 32 55 34
                       L70 34 C78 34 84 40 84 48 C84 54 80 58 74 60
                       L74 90 C74 94 70 96 66 96 L34 96 C30 96 26 94 26 90
                       L26 60 C20 58 16 54 16 48 C16 40 22 34 30 34
                       L45 34 C40 32 36 28 36 22 C36 14 42 8 50 8 Z"
                    fill={`url(#meepleGrad-${index}-${tokenColor.replace('#', '')})`}
                    stroke={adjustColor(tokenColor, -40)}
                    strokeWidth="2"
                />
                {/* Highlight */}
                <ellipse cx="44" cy="20" rx="6" ry="4" fill="rgba(255,255,255,0.3)" />
            </svg>
        );
    };

    const renderCube = (index, tokenColor) => (
        <div 
            key={index}
            style={{
                width: size,
                height: size,
                background: `linear-gradient(145deg, ${adjustColor(tokenColor, 20)} 0%, ${tokenColor} 50%, ${adjustColor(tokenColor, -30)} 100%)`,
                borderRadius: '2px',
                boxShadow: `
                    0 2px 3px rgba(0,0,0,0.4),
                    inset 0 1px 0 rgba(255,255,255,0.4),
                    inset -1px -1px 0 rgba(0,0,0,0.2)
                `,
                border: `1px solid ${adjustColor(tokenColor, -40)}`
            }} 
            title={`${label || 'Kuutio'}`} 
        />
    );

    const renderDisc = (index, tokenColor) => (
        <div 
            key={index}
            style={{
                width: size,
                height: size * 0.35,
                background: `linear-gradient(180deg, ${adjustColor(tokenColor, 20)} 0%, ${tokenColor} 40%, ${adjustColor(tokenColor, -30)} 100%)`,
                borderRadius: '50%',
                boxShadow: `
                    0 2px 3px rgba(0,0,0,0.4),
                    inset 0 1px 0 rgba(255,255,255,0.5)
                `,
                border: `1px solid ${adjustColor(tokenColor, -30)}`
            }} 
            title={`${label || 'Levy'}`} 
        />
    );

    const renderCircle = (index, tokenColor) => (
        <div 
            key={index}
            style={{
                width: size,
                height: size,
                background: `radial-gradient(circle at 35% 35%, ${adjustColor(tokenColor, 40)} 0%, ${tokenColor} 40%, ${adjustColor(tokenColor, -40)} 100%)`,
                borderRadius: '50%',
                boxShadow: `
                    0 2px 3px rgba(0,0,0,0.4),
                    inset 0 1px 0 rgba(255,255,255,0.3)
                `,
                border: `1px solid ${adjustColor(tokenColor, -30)}`
            }} 
            title={`${label || 'Token'}`} 
        />
    );

    const renderResource = (index, tokenColor) => (
        <div 
            key={index}
            style={{
                width: size,
                height: size,
                background: `linear-gradient(135deg, ${adjustColor(tokenColor, 20)} 0%, ${tokenColor} 50%, ${adjustColor(tokenColor, -30)} 100%)`,
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                boxShadow: '0 2px 3px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }} 
            title={`${label || 'Resurssi'}`}
        />
    );

    const renderToken = (index) => {
        const tokenColor = getTokenColor(index);
        
        switch (variant) {
            case 'cube':
                return renderCube(index, tokenColor);
            case 'meeple':
            case 'worker':
                return renderMeeple(index, tokenColor);
            case 'resource':
                return renderResource(index, tokenColor);
            case 'disc':
                return renderDisc(index, tokenColor);
            default: // circle
                return renderCircle(index, tokenColor);
        }
    };

    return (
        <div 
            className={`setup-tokens ${variant} ${isActive ? 'active' : ''}`}
            style={containerStyle}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label || `${count} nappulaa`}
        >
            {Array.from({ length: Math.min(count, 12) }).map((_, i) => renderToken(i))}
            {label && (
                <div style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.55rem',
                    color: '#fff',
                    marginTop: '2px',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}>
                    {label} {count > 1 && `×${count}`}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP PLAYER AREA - Individual player zones with meeple
// ============================================
export const SetupPlayerArea = ({ 
    playerNumber = 1,
    playerCount = 4,
    color = '#3498db',
    name = '',
    position = { x: 50, y: 50 },
    size = { w: 10, h: 12 },
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    arrangement = 'around-table', // around-table, row, column, edge
    theme = {},
    children
}) => {
    // Calculate position based on arrangement
    const getArrangedPosition = () => {
        if (arrangement === 'around-table') {
            const angle = (playerNumber - 1) * (360 / playerCount) - 90;
            const radius = 42;
            return {
                x: 50 + radius * Math.cos(angle * Math.PI / 180),
                y: 50 + radius * Math.sin(angle * Math.PI / 180)
            };
        }
        if (arrangement === 'edge') {
            // Position along edges for 4+ players
            const positions = [
                { x: 50, y: 5 },   // top
                { x: 95, y: 50 },  // right
                { x: 50, y: 95 },  // bottom
                { x: 5, y: 50 },   // left
                { x: 85, y: 10 },  // top-right
                { x: 85, y: 90 },  // bottom-right
                { x: 15, y: 90 },  // bottom-left
                { x: 15, y: 10 },  // top-left
                { x: 30, y: 5 },   // extra positions
                { x: 70, y: 5 },
                { x: 30, y: 95 },
                { x: 70, y: 95 }
            ];
            return positions[(playerNumber - 1) % positions.length];
        }
        return position;
    };

    const pos = getArrangedPosition();

    // Meeple SVG for player
    const renderMeeple = () => (
        <svg 
            width="28" 
            height="28" 
            viewBox="0 0 100 100"
            style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}
        >
            <defs>
                <linearGradient id={`playerMeeple-${playerNumber}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={adjustColor(color, 30)} />
                    <stop offset="50%" stopColor={color} />
                    <stop offset="100%" stopColor={adjustColor(color, -30)} />
                </linearGradient>
            </defs>
            <path 
                d="M50 8 
                   C58 8 64 14 64 22 C64 28 60 32 55 34
                   L70 34 C78 34 84 40 84 48 C84 54 80 58 74 60
                   L74 90 C74 94 70 96 66 96 L34 96 C30 96 26 94 26 90
                   L26 60 C20 58 16 54 16 48 C16 40 22 34 30 34
                   L45 34 C40 32 36 28 36 22 C36 14 42 8 50 8 Z"
                fill={`url(#playerMeeple-${playerNumber})`}
                stroke={adjustColor(color, -40)}
                strokeWidth="2"
            />
            <ellipse cx="44" cy="20" rx="6" ry="4" fill="rgba(255,255,255,0.3)" />
        </svg>
    );

    return (
        <div 
            className={`setup-player-area player-${playerNumber} ${isActive ? 'active' : ''}`}
            style={{
                position: 'absolute',
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                cursor: isHotspot ? 'pointer' : 'default',
                padding: '6px',
                borderRadius: '8px',
                background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.3)',
                boxShadow: isActive 
                    ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}`
                    : 'none',
                transition: 'all 0.3s ease'
            }}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={name || `Pelaaja ${playerNumber}`}
        >
            {renderMeeple()}
            <span style={{ 
                fontSize: '0.55rem', 
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                whiteSpace: 'nowrap'
            }}>
                {name || `P${playerNumber}`}
            </span>
            {children}
        </div>
    );
};

// ============================================
// SETUP DICE - Realistic dice with pips
// ============================================
export const SetupDice = ({ 
    count = 2,
    sides = 6,
    color = '#f5f5f5',
    dotColor = '#1a1a1a',
    values = null, // Array of values to show, or null for random
    position = { x: 50, y: 50 },
    size = 28,
    label = '',
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const containerStyle = {
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        cursor: isHotspot ? 'pointer' : 'default',
        padding: '6px',
        borderRadius: '8px',
        background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.3)',
        boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none',
        transition: 'all 0.3s ease'
    };

    // Generate random values if not provided
    const getValues = () => {
        if (values && values.length >= count) return values.slice(0, count);
        return Array.from({ length: count }, () => Math.floor(Math.random() * sides) + 1);
    };

    const diceValues = getValues();

    // Pip positions for 6-sided dice
    const pipPositions = {
        1: [[50, 50]],
        2: [[25, 25], [75, 75]],
        3: [[25, 25], [50, 50], [75, 75]],
        4: [[25, 25], [75, 25], [25, 75], [75, 75]],
        5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
        6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]]
    };

    const renderDie = (value, index) => {
        const rotation = (index * 15) - 10 + Math.random() * 10;
        
        return (
            <div 
                key={index}
                style={{
                    width: size,
                    height: size,
                    background: `linear-gradient(145deg, ${color} 0%, ${adjustColor(color, -15)} 100%)`,
                    borderRadius: size * 0.15,
                    boxShadow: `
                        0 ${size * 0.1}px ${size * 0.2}px rgba(0,0,0,0.4),
                        inset 0 1px 0 rgba(255,255,255,0.6),
                        inset 0 -1px 0 rgba(0,0,0,0.1)
                    `,
                    position: 'relative',
                    transform: `rotate(${rotation}deg)`,
                    border: '1px solid rgba(0,0,0,0.1)'
                }}
            >
                {sides === 6 && pipPositions[value]?.map(([x, y], pipIndex) => (
                    <div
                        key={pipIndex}
                        style={{
                            position: 'absolute',
                            left: `${x}%`,
                            top: `${y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: size * 0.18,
                            height: size * 0.18,
                            borderRadius: '50%',
                            background: `radial-gradient(circle at 30% 30%, ${dotColor}, ${adjustColor(dotColor, -30)})`,
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)'
                        }}
                    />
                ))}
                {sides !== 6 && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: size * 0.5,
                        fontWeight: 'bold',
                        color: dotColor
                    }}>
                        {value}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div 
            className={`setup-dice ${isActive ? 'active' : ''}`}
            style={containerStyle}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label || `${count} noppaa`}
        >
            <div style={{ display: 'flex', gap: '4px' }}>
                {diceValues.map((val, i) => renderDie(val, i))}
            </div>
            {label && (
                <div style={{
                    fontSize: '0.6rem',
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                }}>
                    {label}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP TILES - Tile placement games (compact)
// ============================================
export const SetupTiles = ({ 
    count = 1,
    shape = 'square', // square, hex, triangle
    color = '#8e44ad',
    label = '',
    position = { x: 50, y: 50 },
    size = 24,
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const containerStyle = {
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        cursor: isHotspot ? 'pointer' : 'default',
        padding: '5px',
        borderRadius: '6px',
        background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.25)',
        boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none',
        transition: 'all 0.3s ease'
    };

    const getTileShape = () => {
        switch (shape) {
            case 'hex':
                return 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';
            case 'triangle':
                return 'polygon(50% 0%, 100% 100%, 0% 100%)';
            default:
                return 'none';
        }
    };

    // Stack tiles slightly offset
    const renderStack = () => {
        const stackCount = Math.min(count > 10 ? 4 : count > 5 ? 3 : count, 4);
        return (
            <div style={{ position: 'relative', width: size + stackCount * 2, height: size + stackCount * 2 }}>
                {Array.from({ length: stackCount }).map((_, i) => (
                    <div 
                        key={i} 
                        style={{
                            position: 'absolute',
                            bottom: i * 2,
                            left: i * 1,
                            width: size,
                            height: size,
                            background: `linear-gradient(145deg, ${adjustColor(color, 20)} 0%, ${color} 50%, ${adjustColor(color, -25)} 100%)`,
                            borderRadius: shape === 'square' ? '3px' : '0',
                            clipPath: getTileShape(),
                            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            border: `1px solid ${adjustColor(color, -30)}`
                        }}
                    />
                ))}
            </div>
        );
    };

    return (
        <div 
            className={`setup-tiles ${shape} ${isActive ? 'active' : ''}`}
            style={containerStyle}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label || `${count} laattaa`}
        >
            {renderStack()}
            {label && (
                <div style={{
                    fontSize: '0.55rem',
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                }}>
                    {label} {count > 1 && `(${count})`}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP COINS - Currency tokens (stacked, realistic)
// ============================================
export const SetupCoins = ({ 
    variant = 'gold', // gold, silver, bronze, copper
    count = 1,
    value = null,
    label = '',
    position = { x: 50, y: 50 },
    size = 18,
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const coinColors = {
        gold: { main: '#ffd700', light: '#ffec8b', dark: '#b8860b', edge: '#996515' },
        silver: { main: '#c0c0c0', light: '#e8e8e8', dark: '#808080', edge: '#606060' },
        bronze: { main: '#cd7f32', light: '#daa06d', dark: '#8b4513', edge: '#654321' },
        copper: { main: '#b87333', light: '#d4956a', dark: '#6b4423', edge: '#4a3020' }
    };

    const colors = coinColors[variant] || coinColors.gold;
    const stackCount = Math.min(count > 20 ? 5 : count > 10 ? 4 : count > 5 ? 3 : count, 5);

    // Render stacked coins
    const renderStack = () => (
        <div style={{ 
            position: 'relative', 
            width: size, 
            height: size + stackCount * 2 
        }}>
            {Array.from({ length: stackCount }).map((_, i) => (
                <div 
                    key={i}
                    style={{
                        position: 'absolute',
                        bottom: i * 2,
                        left: 0,
                        width: size,
                        height: size * 0.3,
                        borderRadius: '50%',
                        background: `linear-gradient(180deg, ${colors.light} 0%, ${colors.main} 40%, ${colors.dark} 100%)`,
                        border: `1px solid ${colors.edge}`,
                        boxShadow: `
                            0 1px 2px rgba(0,0,0,0.4),
                            inset 0 1px 0 rgba(255,255,255,0.4)
                        `
                    }}
                />
            ))}
            {/* Top coin face */}
            <div style={{
                position: 'absolute',
                bottom: stackCount * 2,
                left: 0,
                width: size,
                height: size * 0.3,
                borderRadius: '50%',
                background: `radial-gradient(circle at 35% 35%, ${colors.light} 0%, ${colors.main} 50%, ${colors.dark} 100%)`,
                border: `1px solid ${colors.edge}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.25,
                fontWeight: 'bold',
                color: colors.edge
            }}>
                {value !== null && <span>{value}</span>}
            </div>
        </div>
    );

    return (
        <div 
            className={`setup-coins ${variant} ${isActive ? 'active' : ''}`}
            style={{
                position: 'absolute',
                left: `${position.x}%`,
                top: `${position.y}%`,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                cursor: isHotspot ? 'pointer' : 'default',
                padding: '5px',
                borderRadius: '6px',
                background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.25)',
                boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none',
                transition: 'all 0.3s ease'
            }}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={label || `${count} ${variant} kolikkoa`}
        >
            {renderStack()}
            {(label || count > 1) && (
                <div style={{
                    fontSize: '0.5rem',
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                }}>
                    {label || `×${count}`}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP SCORE MARKERS - Point tracking (+n / -n) - Compact
// ============================================
export const SetupScoreMarkers = ({ 
    value = 0,
    variant = 'badge', // badge, track, dial, counter, chip
    label = '',
    position = { x: 50, y: 50 },
    size = 24,
    color = null, // Custom color override
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {}
}) => {
    const isPositive = value >= 0;
    const markerColor = color || (isPositive ? '#27ae60' : '#e74c3c');

    const formatValue = (v) => {
        if (v > 0) return `+${v}`;
        if (v < 0) return `${v}`;
        return '0';
    };

    // Compact chip style (default)
    const renderChip = () => (
        <div style={{
            minWidth: size,
            height: size,
            padding: '0 6px',
            borderRadius: size / 2,
            background: `linear-gradient(145deg, ${adjustColor(markerColor, 20)} 0%, ${markerColor} 50%, ${adjustColor(markerColor, -25)} 100%)`,
            border: `1px solid ${adjustColor(markerColor, -30)}`,
            boxShadow: `
                0 2px 4px rgba(0,0,0,0.3),
                inset 0 1px 0 rgba(255,255,255,0.3)
            `,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size * 0.45,
            fontWeight: 'bold',
            color: '#fff',
            textShadow: '0 1px 1px rgba(0,0,0,0.5)'
        }}>
            {formatValue(value)}
        </div>
    );

    const renderBadge = () => (
        <div style={{
            minWidth: size * 1.2,
            height: size * 0.8,
            padding: '0 6px',
            borderRadius: '12px',
            background: `linear-gradient(145deg, ${markerColor} 0%, ${adjustColor(markerColor, -25)} 100%)`,
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size * 0.4,
            fontWeight: 'bold',
            color: '#fff'
        }}>
            {formatValue(value)}
        </div>
    );

    const renderDial = () => (
        <div style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: '#1a1a1a',
            border: '2px solid #333',
            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                fontSize: size * 0.35,
                fontWeight: 'bold',
                color: markerColor
            }}>
                {formatValue(value)}
            </div>
        </div>
    );

    const renderCounter = () => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px'
        }}>
            <div style={{
                width: size * 0.5,
                height: size * 0.5,
                borderRadius: '3px',
                background: '#c0392b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.3,
                fontWeight: 'bold',
                color: '#fff'
            }}>−</div>
            <div style={{
                minWidth: size * 0.8,
                height: size * 0.6,
                borderRadius: '3px',
                background: '#222',
                border: '1px solid #444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.35,
                fontWeight: 'bold',
                color: markerColor
            }}>
                {formatValue(value)}
            </div>
            <div style={{
                width: size * 0.5,
                height: size * 0.5,
                borderRadius: '3px',
                background: '#27ae60',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.3,
                fontWeight: 'bold',
                color: '#fff'
            }}>+</div>
        </div>
    );

    return (
        <div 
            className={`setup-score-markers ${variant} ${isActive ? 'active' : ''}`}
            style={{
                position: 'absolute',
                left: `${position.x}%`,
                top: `${position.y}%`,
                transform: 'translate(-50%, -50%)',
                cursor: isHotspot ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                padding: '4px',
                borderRadius: '6px',
                background: isActive ? 'rgba(255,215,0,0.2)' : 'rgba(0,0,0,0.25)',
                boxShadow: isActive ? `0 0 0 2px ${theme.highlightColor || '#ffd700'}` : 'none'
            }}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
            title={`${label || 'Pisteet'}: ${formatValue(value)}`}
        >
            {variant === 'chip' && renderChip()}
            {variant === 'badge' && renderBadge()}
            {variant === 'dial' && renderDial()}
            {variant === 'counter' && renderCounter()}
            {label && (
                <div style={{
                    fontSize: '0.5rem',
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                }}>
                    {label}
                </div>
            )}
        </div>
    );
};

// ============================================
// SETUP CENTRAL AREA - Center of table items
// ============================================
export const SetupCentralArea = ({
    label = '',
    size = { w: 30, h: 30 },
    isHotspot = false,
    hotspotId,
    isActive = false,
    onClick,
    theme = {},
    children
}) => {
    const areaStyle = {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${size.w}%`,
        height: `${size.h}%`,
        minWidth: '120px',
        minHeight: '100px',
        background: 'rgba(0,0,0,0.3)',
        border: `2px dashed ${theme.centralBorder || 'rgba(255,255,255,0.4)'}`,
        borderRadius: '12px',
        boxShadow: isActive 
            ? `0 0 0 3px ${theme.highlightColor || '#ffd700'}`
            : 'none',
        cursor: isHotspot ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px'
    };

    return (
        <div 
            className={`setup-central-area ${isActive ? 'active' : ''}`}
            style={areaStyle}
            onClick={() => isHotspot && onClick?.(hotspotId)}
            data-hotspot={hotspotId}
        >
            {label && (
                <div style={{
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: '#fff',
                    marginBottom: '8px',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                }}>
                    {label}
                </div>
            )}
            {children}
        </div>
    );
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Adjust color brightness
function adjustColor(color, amount) {
    const hex = color.replace('#', '');
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// Player colors palette
export const PLAYER_COLORS = [
    '#e74c3c', // Red
    '#3498db', // Blue
    '#27ae60', // Green
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#e91e63', // Pink
    '#00bcd4', // Cyan
    '#ff5722', // Deep Orange
    '#607d8b', // Blue Grey
    '#795548', // Brown
    '#ffc107'  // Amber
];

// Game theme presets
export const GAME_THEMES = {
    cycling: {
        tableColor: 'linear-gradient(135deg, #2d4a3e 0%, #1a2f27 100%)',
        boardColor: '#666',
        highlightColor: '#ffd700',
        trackColor: '#555'
    },
    medieval: {
        tableColor: 'linear-gradient(135deg, #4a3728 0%, #2d1f15 100%)',
        boardColor: '#d4a574',
        highlightColor: '#ffd700',
        borderColor: '#8b6914'
    },
    space: {
        tableColor: 'linear-gradient(135deg, #0a0a2e 0%, #16163a 100%)',
        boardColor: '#1a1a4a',
        highlightColor: '#00ffff',
        borderColor: '#333366'
    },
    nature: {
        tableColor: 'linear-gradient(135deg, #2d5a27 0%, #1a3d17 100%)',
        boardColor: '#8fbc8f',
        highlightColor: '#90ee90',
        borderColor: '#228b22'
    },
    trading: {
        tableColor: 'linear-gradient(135deg, #3d2b1f 0%, #2a1f15 100%)',
        boardColor: '#deb887',
        highlightColor: '#ffd700',
        borderColor: '#8b4513'
    }
};

const SetupComponents = {
    SetupTable,
    SetupBoard,
    SetupCards,
    SetupTokens,
    SetupPlayerArea,
    SetupDice,
    SetupTiles,
    SetupCoins,
    SetupScoreMarkers,
    SetupCentralArea,
    PLAYER_COLORS,
    GAME_THEMES
};

export default SetupComponents;
